import React, { createContext, useContext, useState, useEffect } from 'react'

export type CampaignStatus = 'pending' | 'live' | 'completed' | 'rejected' | 'bid'

export interface Campaign {
  id: string
  brand: {
    name: string
    logo: string
  }
  title: string
  platform: string
  payout: number
  status: CampaignStatus
  startDate: string
  endDate: string
  brief?: string
  script?: string
  audioUrl?: string
  instructions?: string
  postLink?: string
  submittedAt?: string
  approvedAt?: string
  bidReceivedAt?: string
  notificationShown?: boolean
  dueDateNotificationShown?: boolean
  metrics?: {
    views: number
    engagement: number
    completionRate: number
  }
  paymentStatus?: 'pending' | 'paid'
  paymentDate?: string
  conceptStatus?: 'pending' | 'submitted' | 'approved' | 'rejected' | 'changes_requested'
  videoReviewStatus?: 'pending' | 'sent-for-approval' | 'approved' | 'changes-requested'
  brandComments?: string
  finalVideoStatus?: 'pending' | 'uploaded' | 'posted'
  videoFileName?: string
  selectedPlatforms?: string[]
  conceptData?: {
    integrationConcept: string
    selectedTheme: string
    adRead: string
  }
}

import { mockCampaigns } from '@/data/mockCampaigns'

interface CampaignsContextType {
  campaigns: Campaign[]
  bids: Campaign[]
  acceptBid: (id: string) => void
  rejectBid: (id: string) => void
  submitPost: (id: string, postLink: string) => void
  getCampaign: (id: string) => Campaign | undefined
  refreshCampaigns: () => void
  clearExpiredBids: () => void
  uploadVideoForReview: (id: string, fileName: string) => void
  approveVideoForReview: (id: string, comments: string) => void
  requestVideoChanges: (id: string, comments: string) => void
  uploadFinalVideo: (id: string, data: any) => void
  postFinalVideo: (id: string, platforms: string[]) => void
  submitCreativeConcept: (
    id: string,
    data: { integrationConcept: string; selectedTheme: string; adRead: string },
  ) => void
  approveCreativeConcept: (id: string) => void
  requestConceptChanges: (id: string, comments: string) => void
}

const CampaignsContext = createContext<CampaignsContextType | undefined>(undefined)

export const CampaignsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Filter out bids
  const bids = campaigns.filter((campaign) => campaign.status === 'bid')

  const loadCampaigns = () => {
    setIsLoading(true)
    // Simulate API call
    setTimeout(() => {
      setCampaigns(mockCampaigns)
      setIsLoading(false)
    }, 1000)
  }

  const acceptBid = (id: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? { ...campaign, status: 'pending' as CampaignStatus, notificationShown: true }
          : campaign,
      ),
    )
  }

  const rejectBid = (id: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? { ...campaign, status: 'rejected' as CampaignStatus, notificationShown: true }
          : campaign,
      ),
    )
  }

  const submitPost = (id: string, postLink: string) => {
    const submittedAt = new Date().toISOString()

    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              postLink,
              submittedAt,
              // In a real app, status would remain pending until AI/admin approval
              // Here we automatically approve for demo purposes
              status: 'live' as CampaignStatus,
              approvedAt: new Date().toISOString(),
              dueDateNotificationShown: true,
              // Initialize metrics
              metrics: {
                views: 0,
                engagement: 0,
                completionRate: 0,
              },
            }
          : campaign,
      ),
    )
  }

  const getCampaign = (id: string) => {
    return campaigns.find((campaign) => campaign.id === id)
  }

  const refreshCampaigns = () => {
    // In a real app, this would fetch fresh data from the API
    // For the demo, we'll simulate updating metrics for live campaigns
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.status === 'live' && campaign.metrics) {
          return {
            ...campaign,
            metrics: {
              views: campaign.metrics.views + Math.floor(Math.random() * 500),
              engagement: parseFloat(
                (campaign.metrics.engagement + Math.random() * 0.2).toFixed(1),
              ),
              completionRate: Math.min(
                100,
                campaign.metrics.completionRate + Math.floor(Math.random() * 2),
              ),
            },
          }
        }
        return campaign
      }),
    )
  }

  const clearExpiredBids = () => {
    const now = new Date()
    const EXPIRATION_TIME = 48 * 60 * 60 * 1000 // 48 hours in milliseconds

    setCampaigns((prevCampaigns) =>
      prevCampaigns.filter((campaign) => {
        // If it's not a bid or doesn't have a timestamp, keep it
        if (campaign.status !== 'bid' || !campaign.bidReceivedAt) {
          return true
        }

        const bidTime = new Date(campaign.bidReceivedAt)
        const timeElapsed = now.getTime() - bidTime.getTime()

        // Keep if it hasn't expired yet
        return timeElapsed < EXPIRATION_TIME
      }),
    )
  }

  const uploadVideoForReview = (id: string, fileName: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              videoFileName: fileName,
              videoReviewStatus: 'sent-for-approval' as const,
            }
          : campaign,
      ),
    )
  }

  const approveVideoForReview = (id: string, comments: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              videoReviewStatus: 'approved' as const,
              brandComments: comments,
            }
          : campaign,
      ),
    )
  }

  const requestVideoChanges = (id: string, comments: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              videoReviewStatus: 'changes-requested' as const,
              brandComments: comments,
            }
          : campaign,
      ),
    )
  }

  const uploadFinalVideo = (id: string, data: any) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              finalVideoStatus: 'uploaded' as const,
              videoFileName: data.fileName,
              selectedPlatforms: data.platforms,
            }
          : campaign,
      ),
    )
  }

  const postFinalVideo = (id: string, platforms: string[]) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) =>
        campaign.id === id
          ? {
              ...campaign,
              finalVideoStatus: 'posted' as const,
              status: 'live' as CampaignStatus,
            }
          : campaign,
      ),
    )
  }

  const submitCreativeConcept = (
    id: string,
    data: { integrationConcept: string; selectedTheme: string; adRead: string },
  ) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.id === id) {
          return {
            ...campaign,
            conceptStatus: 'submitted' as const,
            conceptData: data,
          }
        }
        return campaign
      }),
    )
  }

  const approveCreativeConcept = (id: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.id === id) {
          return {
            ...campaign,
            conceptStatus: 'approved' as const,
          }
        }
        return campaign
      }),
    )
  }

  const requestConceptChanges = (id: string, comments: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.id === id) {
          return {
            ...campaign,
            conceptStatus: 'changes_requested' as const,
            brandComments: comments,
          }
        }
        return campaign
      }),
    )
  }

  useEffect(() => {
    loadCampaigns()

    // Set up interval to refresh metrics for demo purposes
    const intervalId = setInterval(refreshCampaigns, 10000)
    return () => clearInterval(intervalId)
  }, [])

  // Mark notifications as shown
  useEffect(() => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign.status === 'bid' && !campaign.notificationShown) {
          return { ...campaign, notificationShown: true }
        }
        return campaign
      }),
    )
  }, [bids.length])

  return (
    <CampaignsContext.Provider
      value={{
        campaigns,
        bids,
        acceptBid,
        rejectBid,
        submitPost,
        getCampaign,
        refreshCampaigns,
        clearExpiredBids,
        uploadVideoForReview,
        approveVideoForReview,
        requestVideoChanges,
        uploadFinalVideo,
        postFinalVideo,
        submitCreativeConcept,
        approveCreativeConcept,
        requestConceptChanges,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  )
}

export const useCampaigns = () => {
  const context = useContext(CampaignsContext)
  if (context === undefined) {
    throw new Error('useCampaigns must be used within a CampaignsProvider')
  }
  return context
}
